// src/templateManager/components/builders/SourceBuilder.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Card,
  Stack,
  TextInput,
  Textarea,
  Select,
  Group,
  ActionIcon,
  Text,
  Collapse,
  Badge,
  TagsInput
} from '@mantine/core';
import {
  IconGripVertical,
  IconChevronDown,
  IconChevronUp,
  IconTrash
} from '@tabler/icons-react';

/**
 * SourceBuilder - Edits individual risk source
 * Supports text and multiselect types
 */
function SourceBuilder({ areaKey, index, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) {
  const { control, watch } = useFormContext();
  const [isExpanded, setIsExpanded] = useState(true);

  const sourceName = watch(`taxonomy.riskAreas.${areaKey}.riskSources.${index}.name`);
  const sourceType = watch(`taxonomy.riskAreas.${areaKey}.riskSources.${index}.type`);

  return (
    <Card withBorder p="md">
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <IconGripVertical size={16} style={{ cursor: 'grab' }} />
            <Text fw={600}>{sourceName || `Source ${index + 1}`}</Text>
            <Badge size="sm" variant="light">{sourceType || 'multiselect'}</Badge>
          </Group>

          <Group gap="xs">
            {/* Move buttons */}
            <ActionIcon
              variant="subtle"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              size="sm"
            >
              <IconChevronUp size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              size="sm"
            >
              <IconChevronDown size={16} />
            </ActionIcon>

            {/* Expand/collapse */}
            <ActionIcon
              variant="subtle"
              onClick={() => setIsExpanded(!isExpanded)}
              size="sm"
            >
              {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
            </ActionIcon>

            {/* Delete */}
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={onRemove}
              size="sm"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Source configuration */}
        <Collapse in={isExpanded}>
          <Stack gap="md" mt="sm">
            {/* Source Name */}
            <Controller
              name={`taxonomy.riskAreas.${areaKey}.riskSources.${index}.name`}
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Source Name"
                  placeholder="e.g., Physical Health Risks"
                  required
                />
              )}
            />

            {/* Source Type */}
            <Controller
              name={`taxonomy.riskAreas.${areaKey}.riskSources.${index}.type`}
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Source Type"
                  data={[
                    { value: 'text', label: 'Text (freeform input)' },
                    { value: 'multiselect', label: 'Multiselect (predefined options)' }
                  ]}
                  required
                />
              )}
            />

            {/* Description */}
            <Controller
              name={`taxonomy.riskAreas.${areaKey}.riskSources.${index}.description`}
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  label="Description"
                  placeholder="Describe this risk source..."
                  minRows={2}
                />
              )}
            />

            {/* Options (for multiselect) */}
            {sourceType === 'multiselect' && (
              <Controller
                name={`taxonomy.riskAreas.${areaKey}.riskSources.${index}.options`}
                control={control}
                render={({ field }) => (
                  <TagsInput
                    {...field}
                    label="Options"
                    placeholder="Type option and press Enter"
                    description="Enter predefined risk options users can select"
                    splitChars={[',']}
                  />
                )}
              />
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}

SourceBuilder.propTypes = {
  areaKey: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  canMoveUp: PropTypes.bool.isRequired,
  canMoveDown: PropTypes.bool.isRequired
};

export default SourceBuilder;
